import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import UniversityManagerLayout from "../../components/layout/UniversityManagerLayout";
import UMUserList from "../../features/university-manager/UMUserList";
import UMUserForm from "../../features/university-manager/UMUserForm";
import UserDetails from "../../features/users/userDetails";
import UserStatusForm from "../../features/users/UserStatusForm";
import PermissionGuard from "../../components/common/PermissionGuard";
import { useNotification } from "../../context/NotificationContext";
import { useTenant } from "../../context/TenantContext";
import { useUniAdminAuth } from "../../context/UniAdminAuthContext";
import { PERMISSIONS } from "../../utils/permissionKeys";
import umService from "../../services/university-admin/umService";

const UMUsers = () => {
  const { t } = useTranslation(["users", "common"]);
  const { can } = useUniAdminAuth();
  const tenantCtx = useTenant();
  const { showSuccess, showError } = useNotification();

  const [showForm, setShowForm] = useState(false);
  const [showStatusForm, setShowStatusForm] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [viewingUser, setViewingUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // eslint-disable-next-line no-unused-vars
  const handleCreate = () => {
    setEditingUser(null);
    setShowForm(true);
  };

  const handleEdit = async (user) => {
    setLoading(true);
    const response = await umService.getUserDemoDetails(user.id, tenantCtx);
    setLoading(false);
    if (response.success) {
      setEditingUser(response.data);
      setShowForm(true);
    } else {
      showError(t("users:messages.loadDetailFailed"));
    }
  };

  const handleView = async (user) => {
    setLoading(true);
    const response = await umService.getUserDemoDetails(user.id, tenantCtx);
    setLoading(false);
    if (response.success) {
      setViewingUser(response.data);
    } else {
      showError(t("users:messages.loadDetailFailed"));
    }
  };

  const handleFormSubmit = async (formData) => {
    setLoading(true);
    const response = editingUser
      ? await umService.updateUserDemo({ id: editingUser.id, ...tenantCtx, data: formData })
      : await umService.createUserDemo({ ...tenantCtx, data: formData });
    setLoading(false);

    if (!response.success) {
      showError(t("users:messages.saveFailed", "Failed to save user"));
      return;
    }

    showSuccess(editingUser ? t("users:messages.updateSuccess") : t("users:messages.createSuccess"));
    setShowForm(false);
    setEditingUser(null);
    setRefreshTrigger((prev) => prev + 1);
  };

  const handleStatusEdit = (user) => {
    setEditingUser(user);
    setShowStatusForm(true);
  };

  const handleStatusFormSubmit = async (formData) => {
    setLoading(true);
    const response = await umService.changeUserDemoStatusAndRole({
      id: editingUser.id,
      ...tenantCtx,
      data: formData,
    });
    setLoading(false);

    if (!response.success) {
      showError(t("users:messages.statusUpdateFailed"));
      return;
    }

    setShowStatusForm(false);
    setEditingUser(null);
    setRefreshTrigger((prev) => prev + 1);
    showSuccess(t("users:messages.statusUpdateSuccess"));
  };

  const handleDelete = (user) => {
    setDeletingUser(user);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    setLoading(true);
    const response = await umService.deleteUserDemo({ id: deletingUser.id, ...tenantCtx });
    setLoading(false);

    if (!response.success) {
      showError(t("users:messages.deleteFailed"));
      return;
    }

    setShowDeleteModal(false);
    setDeletingUser(null);
    setRefreshTrigger((prev) => prev + 1);
    showSuccess(t("users:messages.deleteAccountSuccess"));
  };

  return (
    <UniversityManagerLayout>
      <PermissionGuard allow={can(PERMISSIONS.user.list)}>
      <div className="users-page">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h1 className="h3 mb-1 fw-bold text-dark">{t("users:management")}</h1>
            <p className="text-muted mb-0">{t("users:managementSubtitle")}</p>
          </div>
          {/* TODO: Add User button — will be enabled later
          <button className="btn btn-primary" onClick={handleCreate}>
            <i className="fas fa-plus me-2"></i>
            {t("users:addUser")}
          </button>
          */}
        </div>

        {showForm && (
          <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog modal-xl">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title"><i className={`fas ${editingUser ? "fa-edit" : "fa-plus"} me-2`}></i>{editingUser ? t("users:editUser") : t("users:addNewUser")}</h5>
                  <button type="button" className="btn-close" onClick={() => setShowForm(false)} disabled={loading}></button>
                </div>
                <div className="modal-body">
                  <UMUserForm user={editingUser} onSubmit={handleFormSubmit} onCancel={() => setShowForm(false)} loading={loading} />
                </div>
              </div>
            </div>
          </div>
        )}

        {viewingUser && (
          <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog modal-xl">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title"><i className="fas fa-user me-2"></i>{t("users:userDetails")}</h5>
                  <button type="button" className="btn-close" onClick={() => setViewingUser(null)}></button>
                </div>
                <div className="modal-body"><UserDetails user={viewingUser} onClose={() => setViewingUser(null)} onEdit={handleEdit} /></div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-primary" onClick={() => { setViewingUser(null); handleEdit(viewingUser); }}><i className="fas fa-edit me-2"></i>{t("users:editUser")}</button>
                  <button type="button" className="btn btn-secondary" onClick={() => setViewingUser(null)}>{t("common:buttons.close")}</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showStatusForm && (
          <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title"><i className="fas fa-user-shield me-2"></i>{t("users:manageStatusRole")}</h5>
                  <button type="button" className="btn-close" onClick={() => setShowStatusForm(false)} disabled={loading}></button>
                </div>
                <div className="modal-body"><UserStatusForm user={editingUser} onSubmit={handleStatusFormSubmit} onCancel={() => setShowStatusForm(false)} loading={loading} /></div>
              </div>
            </div>
          </div>
        )}

        {showDeleteModal && (
          <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title text-danger"><i className="fas fa-exclamation-triangle me-2"></i>{t("users:confirmDeletion")}</h5>
                  <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)} disabled={loading}></button>
                </div>
                <div className="modal-body">
                  <div className="alert alert-warning" role="alert"><i className="fas fa-exclamation-triangle me-2"></i><strong>{t("common:messages.warning") || "Warning"}:</strong> {t("users:deleteWarning")}</div>
                  <p>{t("users:deleteConfirmText", { name: deletingUser?.fullName, id: deletingUser?.id })}</p>
                  <p className="text-muted mb-0">{t("users:deleteIrreversible")}</p>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)} disabled={loading}>{t("common:buttons.cancel")}</button>
                  <button type="button" className="btn btn-danger" onClick={handleDeleteConfirm} disabled={loading}>
                    {loading ? <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>{t("common:actions.deleting")}</> : <><i className="fas fa-trash me-2"></i>{t("common:buttons.delete")}</>}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <UMUserList onEdit={handleEdit} onView={handleView} onStatusEdit={handleStatusEdit} onDelete={handleDelete} refreshTrigger={refreshTrigger} />
      </div>
      </PermissionGuard>
    </UniversityManagerLayout>
  );
};

export default UMUsers;
